import React from 'react';

import type { Text } from '@zillow/constellation';
import { DialogActions, Flex, FormField, Label, Paragraph, Textarea, TextButton } from '@zillow/constellation';
import { NumberStepper } from '@zillow/zrs-comet';

import type { Section } from '../../../models/index';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { MAXIMUM_NUMBER_OF_OCCUPANTS } from '../../../constants/index';
import type { HTMLElementEvent } from '../../../models/Types';
import InlineTooltip from '../../Widgets/InlineTooltip';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function MaxmimumNumberOfOccupants_v3({ section, setSectionValues }: Props): JSX.Element {
  function onNoOutstandingViolations(): void {
    setSectionValues({
      [MAXIMUM_NUMBER_OF_OCCUPANTS.FIELD_NAME.OUTSTANDING_VIOLATIONS]: false,
    });
  }

  function onYesOutstandingViolations(): void {
    setSectionValues({
      [MAXIMUM_NUMBER_OF_OCCUPANTS.FIELD_NAME.OUTSTANDING_VIOLATIONS]: true,
    });
  }

  function onDescriptionChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [MAXIMUM_NUMBER_OF_OCCUPANTS.FIELD_NAME.OUTSTANDING_VIOLATIONS_DESCRIPTION]: value,
    });
  }

  function onMaximumOccupancyChange(value: number): void {
    setSectionValues({
      [MAXIMUM_NUMBER_OF_OCCUPANTS.FIELD_NAME.MAXIMUM_NUMBER_OF_OCCUPANTS]: value,
    });
  }

  const outstandingViolationsEnabled = section.getFieldValue(
    MAXIMUM_NUMBER_OF_OCCUPANTS.FIELD_NAME.OUTSTANDING_VIOLATIONS,
  );
  const outstandingViolationsDescription = section.getFieldValue(
    MAXIMUM_NUMBER_OF_OCCUPANTS.FIELD_NAME.OUTSTANDING_VIOLATIONS_DESCRIPTION,
  );
  const maximumOccupants = Number(
    section.getFieldValue(MAXIMUM_NUMBER_OF_OCCUPANTS.FIELD_NAME.MAXIMUM_NUMBER_OF_OCCUPANTS),
  );

  return (
    <>
      <FormField
        marginBottom="sm"
        label={<Label size="lg">What is the maximum occupancy of the premises?</Label>}
        control={
          <Flex display="flex" alignItems="center" flexDirection="column">
            <NumberStepper min={1} max={99} value={maximumOccupants} onChange={onMaximumOccupancyChange} />
            <Paragraph>people allowed</Paragraph>
          </Flex>
        }
      />
      <FormField
        label={
          <Label>
            Is the property subject to any outstanding violations of the{' '}
            <InlineTooltip
              tooltipBodyText={
                <>
                  As per local law, the City Housing Conservation District Inspection is a basic code inspection
                  designed to check for minimal interior building code violations, and meet minimum exterior standards
                  under the International Property Maintenance Code.
                  <DialogActions>
                    <TextButton
                      as="a"
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://www.stlouis-mo.gov/government/departments/public-safety/building/housing-conservation.cfm"
                    >
                      Learn more
                    </TextButton>
                  </DialogActions>
                </>
              }
            >
              City Housing Conservation Program?
            </InlineTooltip>
          </Label>
        }
        control={
          <YesAndNoExpansion
            yesHandler={onYesOutstandingViolations}
            noHandler={onNoOutstandingViolations}
            yesActive={outstandingViolationsEnabled}
          >
            <FormField
              label={<Label>Describe these outstanding violations</Label>}
              control={
                <Textarea resize="vertical" value={outstandingViolationsDescription} onChange={onDescriptionChange} />
              }
            />
          </YesAndNoExpansion>
        }
      />
    </>
  );
}
